/* global window, RTCPeerConnection, RTCPeerConnectionWithLocalStreams */
import userAgent, { Browser, IPlatformParams } from '../../utils/userAgent';
import { notify } from '../errorReporter';
import { ITrackMids } from '@engoo/realtime-protocol/signaling/negotiation';
import { WebRTCFeatures } from 'app/services/rtc/constants';
import { IStreams } from 'app/services/rtc/Streams';
import { Set } from 'immutable';

interface IWebRTCFeatureDetectionDeps {
  platform: Pick<IPlatformParams, 'browser' | 'version'>,
  PeerConnectionImpl?: { prototype?: Partial<RTCPeerConnection> },
}

function getSupportedFeatures({
  platform = userAgent(),
  PeerConnectionImpl = window.RTCPeerConnection as (typeof RTCPeerConnection | undefined),
}: Partial<IWebRTCFeatureDetectionDeps> = {}): Set<WebRTCFeatures> {
  return Set<WebRTCFeatures>().withMutations((features) => {
    if (PeerConnectionImpl) {
      if (supportsLocalStreamsAPI(PeerConnectionImpl.prototype)) {
        features.add(WebRTCFeatures.LegacyStreams);
      }

      if (supportsUnifiedPlan({ platform, PeerConnectionImpl })) {
        features.add(WebRTCFeatures.UnifiedPlan);
      }

      // Only claim to support WebRTC if at least one of legacy
      // streams or unified plan is supported
      if (
        features.has(WebRTCFeatures.LegacyStreams) ||
        features.has(WebRTCFeatures.UnifiedPlan)
      ) {
        features.add(WebRTCFeatures.PeerConnection);
      }
    }
  });
}

function supportsLocalStreamsAPI(
  connection?: Partial<RTCPeerConnection>,
): connection is RTCPeerConnectionWithLocalStreams {
  return connection?.addStream !== undefined &&
    connection?.getLocalStreams !== undefined;
}

function supportsUnifiedPlan({ platform, PeerConnectionImpl }: IWebRTCFeatureDetectionDeps) {
  // Chrome versions before 72 did not support Unified Plan, or supported it with
  // buggy implementations, so force older versions to use Plan B.
  const usePlanB = platform.browser === Browser.Chrome && platform.version[0] > 0 && platform.version[0] < 72;
  return Boolean(PeerConnectionImpl?.prototype?.addTransceiver) && !usePlanB;
}

function createPeerConnection({ iceServers = [], ...rest }: RTCConfiguration = {}) {
  let connection;
  const supportedIceServers = filterSupportedIceServers(iceServers);

  try {
    connection = new RTCPeerConnection({
      iceServers: supportedIceServers,
      // Chrome-only
      sdpSemantics: getSupportedFeatures().has(WebRTCFeatures.UnifiedPlan) ? 'unified-plan' : 'plan-b',
      ...rest,
    });
  } catch (error) {
    connection = new RTCPeerConnection({
      iceServers: spreadIceServerUrls(supportedIceServers),
      ...rest,
    } as any as RTCConfiguration);
  }

  return connection;
}

// This is necessary for Edge. It implements an old spec that assumes
// that each server description has only one url, spec allows for an
// array of urls for each server
function spreadIceServerUrls(iceServers: RTCIceServer[]): RTCIceServer[] {
  const result: RTCIceServer[] = [];

  for (const { urls, ...rest } of iceServers) {
    for (const url of urls) {
      result.push({ urls: url, ...rest });
    }
  }

  return result;
}

// Edge doesn't support turns
function filterSupportedIceServers(iceServers: RTCIceServer[]): RTCIceServer[] {
  const supportsTurns = userAgent().browser !== Browser.Edge;
  if (!supportsTurns) {
    const noTurns = (url: string) => !url.startsWith('turns:');
    return filterIceServers(iceServers, noTurns);
  }
  return iceServers;
}

function filterIceServers(iceServers: RTCIceServer[], condition: (url: string) => boolean): RTCIceServer[] {
  return iceServers
    .map(({ urls, ...rest }) => {
      const normalizedUrls = Array.isArray(urls) ? urls : [urls];
      return {
        ...rest,
        urls: normalizedUrls.filter(condition),
      };
    })
    .filter(iceServer => iceServer.urls.length > 0);
}

async function createOffer(
  connection: RTCPeerConnection,
  { audio, video }: { audio: boolean, video: boolean },
  remoteMids: ITrackMids,
) {
  // Chrome throws errors when trying to use the new transceivers API while
  // using Plan B, so use the old API on versions of Chrome that use Plan B
  if (getSupportedFeatures().has(WebRTCFeatures.UnifiedPlan)) {
    // When creating an offer for the first time, we need to add
    // tranceivers for the channels we anticipate if they don't exist
    // already. This will ensure that the offer created by this
    // connection will announce to at least receive data.
    // Add at least two transceivers per kind to accommodate for
    // microphone / camera and screen sharing
    const remoteAudioTransceivers = 1
      + (remoteMids.screenAudio !== undefined ? 1 : 0);

    while (audio && findTransceivers(connection, 'audio').length < remoteAudioTransceivers) {
      connection.addTransceiver('audio', { direction: 'recvonly' });
    }

    const remoteVideoTransceivers = 1
      + (remoteMids.screenVideo !== undefined ? 1 : 0);

    while (video && findTransceivers(connection, 'video').length < remoteVideoTransceivers) {
      connection.addTransceiver('video', { direction: 'recvonly' });
    }

    return await connection.createOffer();
  } else {
    // For browsers that don't support transceivers (non-Chromium Edge at
    // the moment) we need to pass these flags for the offer to
    // announce to at least receive data
    return await connection.createOffer({
      ...audio && { offerToReceiveAudio: true },
      ...video && { offerToReceiveVideo: true },
    });
  }
}

async function createAnswer(connection: RTCPeerConnection) {
  // We don't have any special compatibility handling here but added
  // for the sake of symmetry with createOffer
  return await connection.createAnswer();
}

export type TrackTransceivers = { [K in keyof ITrackMids]?: RTCRtpTransceiver };
export type IEnabledChannels = { audio: boolean, video: boolean };

const setTrack = async (
  connection: RTCPeerConnection,
  track?: MediaStreamTrack,
  stream?: MediaStream,
  transceiver?: RTCRtpTransceiver,
): Promise<RTCRtpTransceiver | undefined> => {
  if (!track || !stream) {
    return transceiver;
  }

  if (transceiver) {
    if (transceiver.sender.track !== track) {
      await transceiver.sender.replaceTrack(track);
    }
    return transceiver;
  }

  // Use `addTrack` instead of `addTransceiver` because `addTrack`
  // will attempt to reuse an existing transceiver, while
  // `addTransceiver` always adds a new one. This can result in more
  // transceivers on the answerer side than on the offerer side and
  // cause infinite renegotiation attempts from the answerer side.
  // Passing stream is only for old Edge compatibility, clients
  // that support unified plan ignore it and group tracks based
  // on transceiver mids
  const sender = connection.addTrack(track, stream);

  // Return transceiver that the sender is associated with
  return connection.getTransceivers().find((tx) => tx.sender === sender);
};

/**
 * Sets tracks from streams on a connection.
 *
 * Reuses `currentTransceivers` when passed in. Otherwise will try
 * to use one of the transceivers that is not sending any data yet.
 *
 * Calling this repetitively with the same set of tracks and
 * transceivers is a no-op.
 *
 * Ignores all audio tracks if `channels.audio` is `false` and all
 * video tracks if `channels.video` is `false`.
 *
 * If unified plan is not supported, it will attempt to use old
 * `addStream` API. This approach sends only one audio and one video
 * track.
 *
 * @returns transceivers that were used to set tracks from current
 * set of `streams`. Returns empty object if unified plan is not
 * supported.
 */
const setConnectionTracks = async (
  connection: RTCPeerConnection,
  currentTransceivers: TrackTransceivers,
  channels: IEnabledChannels,
  streams: IStreams,
): Promise<TrackTransceivers> => {
  const transceivers: TrackTransceivers = {};

  if (getSupportedFeatures().has(WebRTCFeatures.UnifiedPlan)) {
    if (channels.audio) {
      transceivers.audio = await setTrack(
        connection,
        streams.audio?.getAudioTracks()[0],
        streams.audio,
        currentTransceivers.audio,
      );

      transceivers.screenAudio = await setTrack(
        connection,
        streams.screen?.getAudioTracks()[0],
        streams.screen,
        currentTransceivers.screenAudio,
      );
    }

    if (channels.video) {
      transceivers.video = await setTrack(
        connection,
        streams.video?.getVideoTracks()[0],
        streams.video,
        currentTransceivers.video,
      );

      transceivers.screenVideo = await setTrack(
        connection,
        streams.screen?.getVideoTracks()[0],
        streams.screen,
        currentTransceivers.screenVideo,
      );
    }
  } else if (supportsLocalStreamsAPI(connection)) {
    for (const stream of [streams.audio, streams.video]) {
      if (stream && !connection.getLocalStreams().find(s => s === stream)) {
        // In theory this should also remove all the streams that
        // were closed. (When switching devices for example.) This
        // has some problems with Edge for microphones.
        //
        // Only consequence of not removing those streams is that
        // clients with support for unified plans will reject SDPs,
        // causing a reset of the connection, if there are two audio
        // or video tracks. This should be a reasonable trade-off
        // because non-unified-clients are deprecated as long as
        // they're able to change devices _at all_, it's fine.
        connection.addStream(stream);
      }
    }
  } else {
    notify(new Error('Unable to set connection input stream on RTCPeerConnection.'));
  }

  return transceivers;
};

const findTransceivers = (connection: RTCPeerConnection, kind: string) =>
  connection.getTransceivers().filter(({ receiver: { track } }) => !!track && track.kind === kind);

// Export an object for easy stubbing
export default {
  getSupportedFeatures,
  createPeerConnection,
  setConnectionTracks,
  createOffer,
  createAnswer,
  filterIceServers,
};
